import { createRoot } from 'react-dom/client'
import client from './apollo-client';
import { FetchResult } from 'apollo-link';
import { connection } from './messenger/connection';
import { __, setLocale } from './utils';

export type WidgetConnectParams = {
  postParams?: {
    source?: string | any;
    [x: string]: any;
  };
  connectMutation(event: MessageEvent): Promise<FetchResult<Record<string, any> | unknown> | void>;
  connectCallback(responseData: FetchResult['data']): void;
  AppContainer: JSX.ElementType;
}

// base connect function for all widgets
const widgetConnect = (params: WidgetConnectParams) => {
  const { postParams, connectMutation, connectCallback, AppContainer } = params;

  const root = createRoot(document.getElementById('root')!);
  
  window.addEventListener('message', event => {
    // connect to api using passed setting
    if (!(event.data.fromPublisher && event.data.setting)) {
      return;
    }

    root.render(<LoadingIcon />);
    
    connectMutation(event)
      .then(async (response) => {
        const { ApolloProvider } = await import('react-apollo');
        if (!response) throw response;
        
        const { data } = response;
        connectCallback(data);

        // notify parent window that connected
        window.parent.postMessage(
          {
            fromErxes: true,
            ...postParams,
            message: 'connected',
            connectionInfo: data,
            setting: event.data.setting
          },
          '*'
        );

        
        const head = document.querySelector('head');
        if (head) {
          const style = document.createElement('style');
          style.appendChild(
            document.createTextNode(event.data.setting.css || '')
          );
          head.appendChild(style);
        }

        // render the passed container in the chatbot root
        root.render(
          <ApolloProvider client={client}>
            <AppContainer />
          </ApolloProvider>
        );
      })
      .catch( async (error) => {
        const browserLanguage = window.navigator?.language?.split('-')?.[0]
        await setLocale(browserLanguage?.toLowerCase() ||  'es');
        
        root.render(<div style={{
          display:'grid',
          width:'100%',
          height:'100%',
          placeItems:'center',
          fontWeight:'bold',
          color:"#667"
        }}>
          <span>{__("We're sorry, the platform is currently out of service")}.<br/>
        {__("Please try again later")}.</span>
        </div>)
      });
  });
};

const LoadingIcon = () => (
  <div style={{
    display: 'grid',
    placeContent: 'center',
    width: 'calc(100vw - 8px)', // prevents default body margin
    height: 'calc(100vh - 16px)', // prevents default body margin (top and bottom)
  }}>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="50" height="50">
      <circle fill="#3675eb" stroke="#3675eb" strokeWidth="2" r="4" cx="10" cy="25">
        <animate
          attributeName="opacity"
          calcMode="spline"
          dur="0.8s"
          values="1;0;1"
          keySplines=".5 0 .5 1;.5 0 .5 1"
          repeatCount="indefinite"
          begin="-.4s"
        />
      </circle>
      <circle fill="#3675eb" stroke="#3675eb" strokeWidth="2" r="4" cx="25" cy="25">
        <animate
          attributeName="opacity"
          calcMode="spline"
          dur="0.8s"
          values="1;0;1"
          keySplines=".5 0 .5 1;.5 0 .5 1"
          repeatCount="indefinite"
          begin="-.2s"
        />
      </circle>
      <circle fill="#3675eb" stroke="#3675eb" strokeWidth="2" r="4" cx="40" cy="25">
        <animate
          attributeName="opacity"
          calcMode="spline"
          dur="0.8s"
          values="1;0;1"
          keySplines=".5 0 .5 1;.5 0 .5 1"
          repeatCount="indefinite"
          begin="0s"
        />
      </circle>
    </svg>
  </div>
)

export default widgetConnect;
